import { forwardRef, Inject, Injectable } from "@angular/core";
import { ServiceMessageBrokerFactory } from "shared/provider-webworker";
import { TranslateService } from "@ngx-translate/core";

import { OpsIntLookupService } from "./OpsIntLookupService";
import { PrimaryBlotterLookupService } from "../../impl/PrimaryBlotterLookupService";
import { IOpsIntPromiseLookupService } from "../IOpsIntPromiseLookupService";

import { ActiveUserProfilePromiseService } from "shared/auth-services";

// import { NotificationTransactionAdapter } from "../../../adapter/interventionBlotter/NotificationTransactionAdapter";
import { ActiveUser } from "../../../model/core/ActiveUser";
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../../model/core/notifications/BFXNotificationSubType";
import { NotificationService } from "../../notification/impl/NotificationService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { RawDataOpsIntAdapter } from "../../../mediator/adapter/RawDataOpsIntAdapter";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BFXNotificationEventType } from "../../../model/core/notifications/BFXNotificationEventType";
import { OrderDataPrivatizationService } from "../../../model/privatization/OrderDataPrivatizationService";
import { DealPrivatizationService } from "../../../model/privatization/DealPrivatizationService";
import { StaticDataPrivatizationType } from "../../../model/privatization/enum/StaticDataPrivatizationType";
import { OpsIntEventPrivatizer } from "../../../model/privatization/ops-int/OpsIntEventPrivatizer";

enum OpsIntVisibilityType {
  NONE = 0,
  FEED,
  GLOBAL,
  ORDER,
  DEAL,
}

@Injectable()
export class OpsIntPrimaryLookupService
  extends PrimaryBlotterLookupService<string, CubiqTS.OpsIntEventImpl>
  implements IOpsIntPromiseLookupService
{
  protected providerDownMap: Map<string, { provider: string; feedDown: boolean }> = new Map();

  protected feedNotificationExceptionType: Set<CubiqTS.OpsExceptionType> = new Set([
    CubiqTS.OpsExceptionType.FEED_FAILED,
    CubiqTS.OpsExceptionType.FEED_RECOVERED,
  ]);
  // uncatagorized types will have deal, then order checked for visibility
  // absent both deal and orderId will be treated as global
  protected globalOpsExceptionType: Set<CubiqTS.OpsExceptionType> = new Set([
    CubiqTS.OpsExceptionType.AUTO_CANCEL_REJECTED,
    CubiqTS.OpsExceptionType.AUTO_REJECTED_ORDER,
    CubiqTS.OpsExceptionType.BM_HEDGE_FAIL,
    CubiqTS.OpsExceptionType.BM_SWEEP_SECONDARY_REPRICE,
    CubiqTS.OpsExceptionType.BM_SWEPT_HEDGE_ORDER_FILL,
    CubiqTS.OpsExceptionType.EXECUTION_STATUS_DOWN_ERROR,
    CubiqTS.OpsExceptionType.EXECUTION_STATUS_UP,
    CubiqTS.OpsExceptionType.HUB_PROTO_ERROR,
    CubiqTS.OpsExceptionType.ORDER_MIGRATION_EXECUTED,
    CubiqTS.OpsExceptionType.ORDER_MIGRATION_HALTED,
    CubiqTS.OpsExceptionType.ORDER_MIGRATION,
    CubiqTS.OpsExceptionType.TG_STATUS_DOWN,
  ]);

  protected dealOpsExceptionType: Set<CubiqTS.OpsExceptionType> = new Set([
    CubiqTS.OpsExceptionType.BOOKING_ERROR,
    CubiqTS.OpsExceptionType.FAILED_SENDING,
    CubiqTS.OpsExceptionType.FAILED_TO_BOOK,
    CubiqTS.OpsExceptionType.FILL_ACTIVATION_FAIL,
    CubiqTS.OpsExceptionType.FILL_VALUE_DATE_MISMATCH,
    CubiqTS.OpsExceptionType.NACK_RECEIVED_CLEAR_ONLY,
    CubiqTS.OpsExceptionType.NACK_RECEIVED,
    CubiqTS.OpsExceptionType.NO_ACK_RECEIVED,
  ]);

  protected orderOpsExceptionType: Set<CubiqTS.OpsExceptionType> = new Set([
    CubiqTS.OpsExceptionType.ACTIVATE_ORDER_NOT_KNOWN,
    CubiqTS.OpsExceptionType.AUTO_CANCEL_REJECT,
    CubiqTS.OpsExceptionType.AUTO_PAUSE_REJECT,
    CubiqTS.OpsExceptionType.AUTO_PAUSE_TIMEOUT,
    CubiqTS.OpsExceptionType.AUTOFILL_FAILED_TO_TRIGGER,
    CubiqTS.OpsExceptionType.AUTOFILL_TRIGGER_UNKNOWN_PAYLOAD,
    CubiqTS.OpsExceptionType.AUTOFILL_TRIGGER_UNKNOWN_REF,
    CubiqTS.OpsExceptionType.BM_HEDGE_ORDER_REJECT,
    CubiqTS.OpsExceptionType.BM_HEDGE_ORDER_SWEPT,
    CubiqTS.OpsExceptionType.BM_SWEPT_HEDGE_ORDER_FILL,
    CubiqTS.OpsExceptionType.CANCEL_ORDER_NOT_KNOWN,
    CubiqTS.OpsExceptionType.ENTRY_PRICING,
    CubiqTS.OpsExceptionType.EXPIRY_ORDER_NOT_KNOWN,
    CubiqTS.OpsExceptionType.EXT_CANCEL_ERROR,
    CubiqTS.OpsExceptionType.FAILED_TO_PRICE,
    CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER_MANUAL,
    CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER,
    CubiqTS.OpsExceptionType.FILL_MAKER_CANCEL_RCVD,
    CubiqTS.OpsExceptionType.FILL_ORDER_NOT_KNOWN,
    CubiqTS.OpsExceptionType.MAKER_ACCEPT,
    CubiqTS.OpsExceptionType.MAKER_FILL_ORDER_STILL_ACTIVE,
    CubiqTS.OpsExceptionType.MAKER_REJECT,
    CubiqTS.OpsExceptionType.MARKUP_CALCULATION,
    CubiqTS.OpsExceptionType.NO_RESPONSE_CANCEL,
    CubiqTS.OpsExceptionType.NO_RESPONSE_MODIFY,
    CubiqTS.OpsExceptionType.NO_RESPONSE_OCR,
    CubiqTS.OpsExceptionType.NO_RESPONSE_PLACE,
    CubiqTS.OpsExceptionType.NO_RESPONSE_STEAL,
    CubiqTS.OpsExceptionType.NO_RESPONSE_TRANSFER,
    CubiqTS.OpsExceptionType.ORDER_ENTRY,
    CubiqTS.OpsExceptionType.ORDER_NOT_KNOWN,
    CubiqTS.OpsExceptionType.SWEPT_PAST_ROLLOVER,
    CubiqTS.OpsExceptionType.TAKER_NAK_EXEC_RPT,
    CubiqTS.OpsExceptionType.UNEXPECTED_MAKER_ACTIVATE,
    CubiqTS.OpsExceptionType.UNEXPECTED_MAKER_EXPIRY,
    CubiqTS.OpsExceptionType.UNEXPECTED_MAKER_FILL,
    CubiqTS.OpsExceptionType.AUTO_CANCEL_TIMEOUT,
    CubiqTS.OpsExceptionType.UNEXPECTED_MAKER_ROLLBACK,
    CubiqTS.OpsExceptionType.FILL_ADJUSTED_VALUE_DATE,
  ]);

  protected ignoredOpsExceptionType: Set<CubiqTS.OpsExceptionType> = new Set([
    CubiqTS.OpsExceptionType.BM_HEDGE_ORDER_TIMEOUT_FAIL,
    CubiqTS.OpsExceptionType.BM_SWEEP_STARTED,
    CubiqTS.OpsExceptionType.COMP_STATUS_DOWN_ERROR,
    CubiqTS.OpsExceptionType.EXT_CANCEL_REPLACE_ERROR,
    CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER_LIMIT,
    CubiqTS.OpsExceptionType.FEED_MIGRATED,
    CubiqTS.OpsExceptionType.PAUSE_ORDER_NOT_KNOWN,
    CubiqTS.OpsExceptionType.RATE_STATUS_DOWN_ERROR,
    CubiqTS.OpsExceptionType.RATE_STATUS_UP,
    CubiqTS.OpsExceptionType.RESUME_ORDER_NOT_KNOWN,
    CubiqTS.OpsExceptionType.TG_STATUS_UP,
    CubiqTS.OpsExceptionType.UNEXPECTED_MAKER_REJECT,
  ]);

  activeUser: ActiveUser;

  protected queuedEvents: Array<CubiqTS.OpsIntEventImpl> = [];

  constructor(
    protected base: OpsIntLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    @Inject(forwardRef(() => RawDataOpsIntAdapter)) dataAdapter,
    protected activeUserService: ActiveUserProfilePromiseService,
    _translate: TranslateService,
    private notificationService: NotificationService,
    protected dealPrivatizationService: DealPrivatizationService,
    protected orderDataPrivatizationService: OrderDataPrivatizationService,
  ) {
    super(
      base,
      factory,
      CubiqTS.OpsIntEventImpl,
      cubiqWorker,
      "OpsIntEventImpl",
      CubiqTS.OpsIntEventImpl,
      dataAdapter,
      notificationService,
    );
    Promise.all<any>([
      this.activeUserService.getActiveUserData(),
      cubiqWorker.isAuth(`${this.constructor.name}`),
    ]).then(([activeUser]) => {
      let request = new CubiqTS.OpsExceptionRequestImpl();
      let standardReportPrimeComplete: boolean = false;

      this.activeUser = activeUser;

      this.cubiqWorker
        .postServiceRequest(new CubiqTS.OpsFilterCategoryRequestImpl())
        .then((data: CubiqTS.OpsFilterCompositeDataImpl) => {
          this.base.primeOpsFilterCompositeDate(data);

          this.cubiqWorker
            .getEncodableObservable(CubiqTS.OpsIntEventImpl.name)
            .subscribe((opsInterventionEvent: CubiqTS.OpsIntEventImpl) => {
              if (this.primed === true) {
                try {
                  this.processOpsIntEvent(opsInterventionEvent, true);
                } catch (e) {
                  logger.out.error(
                    `${this.constructor.name}.processOpsIntEvent(${opsInterventionEvent}) failed with excpetion: ${e}`,
                  );
                }
              } else {
                this.queuedEvents.push(opsInterventionEvent);
              }
            });

          this.cubiqWorker.postStandardReportServiceRequest(request).subscribe(
            (response) => {
              if (response != null && Array.isArray(response.results)) {
                // regenerate event container to match live event signature (and namesake object)
                response.results.forEach((info: CubiqTS.OpsExcInfoImpl) => {
                  let opsIntEvent = Object.assign(new CubiqTS.OpsIntEventImpl(), {
                    info: info,
                    // these match server side
                    eventId: info.exceptionId,
                  });

                  this.processOpsIntEvent(opsIntEvent, false);
                });
              }

              if (response.isLastReport) {
                standardReportPrimeComplete = true;
              }
            },
            (error) => {
              logger.out.error(`${this.constructor}() %O`, error);
            },
            () => {
              if (standardReportPrimeComplete === false) {
                logger.out.warn(`${this.constructor}() no standard report primed`);
              }

              this.flushPendingUpdates();

              this.base.primeCompleted();
              this.primed = true;
              this._primed.resolve(true);
            },
          );
        });
    });
  }

  private processOpsIntEvent(event: CubiqTS.OpsIntEventImpl, notify: boolean) {
    if (event.info != null && event.info instanceof CubiqTS.OpsExcInfoImpl) {
      let exceptionInfo: CubiqTS.OpsExcInfoImpl = event.info;
      if (exceptionInfo.opsException) {
        let exceptionRecord: CubiqTS.OpsExcRecordImpl = exceptionInfo.opsException;
        let visibilityType = OpsIntVisibilityType.NONE;

        if (exceptionRecord.exceptionState === CubiqTS.OpsExceptionState.CLEARED) {
          this.base.deleteItem(event);
          this.fireModelDeleteEvent(event.eventId.toString(), true);
        } else {
          visibilityType = this.getVisibilityType(event);

          if (OpsIntVisibilityType.NONE != visibilityType) {
            let canSeeOpsInt = this.activeUser.canSeeOpsInt(event, exceptionRecord);
            if (
              (OpsIntVisibilityType.ORDER === visibilityType ||
                OpsIntVisibilityType.DEAL === visibilityType) &&
              canSeeOpsInt
            ) {
              event = this.processModelPrivatization(
                event,
                visibilityType,
                exceptionRecord.dealId,
                exceptionRecord.orderId,
              );
            } else if (canSeeOpsInt == false) {
              logger.out.info(
                `${this.constructor.name} no visibility on event ${event.eventId.toString()} `,
              );
            }

            if (canSeeOpsInt) {
              this.base.mapItem(event);
              if (notify) {
                this.fireModelUpdateEvent(event);
              }
            }
          }
        }

        if (
          OpsIntVisibilityType.FEED === visibilityType &&
          exceptionRecord.descriptionTxt != null &&
          exceptionRecord.exceptionState != CubiqTS.OpsExceptionState.CLEARED
        ) {
          let errorMessage = exceptionRecord.descriptionTxt.errorMessages[0];
          let key: string = JSON.parse(errorMessage.toString())["args"][0];
          if (key !== undefined) {
            event["providerCode"] = key;
            if (exceptionRecord.exceptionTypeId === CubiqTS.OpsExceptionType.FEED_FAILED) {
              if (!this.providerDownMap.get(key)) {
                this.providerDownMap.set(key, {
                  provider: key,
                  feedDown: true,
                });
                if (this.notificationService.shouldNotify(BFXNotificationType.STREAM_OFF)) {
                  this.fireRateEventNotificationEvent(event);
                }
              }
            } else if (
              exceptionRecord.exceptionTypeId === CubiqTS.OpsExceptionType.FEED_RECOVERED
            ) {
              if (this.providerDownMap.get(key)) {
                this.providerDownMap.delete(key);
                this.notificationService.fireNotificationClearEvent(
                  this.notificationService.dataToClearEvent([key]),
                  BFXNotificationEventType.OPS_INTERVENTION_UPDATE_EVENT,
                );
                // this.fireModelNotificationClearEvent([key]);
              }
            }
          }
        }
      }
    }

    if (
      notify === true &&
      event.opsErrors != null &&
      event.opsErrors instanceof CubiqTS.ErrorsImpl &&
      Array.isArray(event.opsErrors.errorMessages) &&
      event.opsErrors.errorMessages.length > 0
    ) {
      this.fireModelErrorEvent(event);
    }
  }

  private getVisibilityType(event: CubiqTS.OpsIntEventImpl): OpsIntVisibilityType {
    if (
      event != null &&
      event instanceof CubiqTS.OpsIntEventImpl &&
      event.info != null &&
      event.info instanceof CubiqTS.OpsExcInfoImpl &&
      event.info.opsException != null &&
      event.info.opsException instanceof CubiqTS.OpsExcRecordImpl
    ) {
      let exceptionTypeId = event.info.opsException.exceptionTypeId;
      if (this.feedNotificationExceptionType.has(exceptionTypeId)) {
        return OpsIntVisibilityType.FEED;
      }

      if (this.globalOpsExceptionType.has(exceptionTypeId)) {
        return OpsIntVisibilityType.GLOBAL;
      }

      if (this.orderOpsExceptionType.has(exceptionTypeId)) {
        // TODO match against visibility related fields and
        // profile returning OpsIntVisibilityType.NONE if checks fail
        // lack of allocation data is a concern on ops int order data is a concern.
        return OpsIntVisibilityType.ORDER;
      }

      if (this.dealOpsExceptionType.has(exceptionTypeId)) {
        // TODO match against visibility related fields and
        // profile returning OpsIntVisibilityType.NONE if checks fail
        return OpsIntVisibilityType.DEAL;
      }
      if (!this.ignoredOpsExceptionType.has(exceptionTypeId)) {
        logger.out.error(
          `oipls - Ignored Ops exception (id:${exceptionTypeId}) not found on Ignore list - please add`,
        );
      }
    }
    return OpsIntVisibilityType.NONE;
  }

  private processModelPrivatization(
    event: CubiqTS.OpsIntEventImpl,
    type: OpsIntVisibilityType,
    dealId: CubiqTS.Long,
    orderId: CubiqTS.Long,
  ) {
    if (event.info != null && event.info instanceof CubiqTS.OpsExcInfoImpl) {
      let label: string;
      let labelSet = new Set<string>();
      // client account or clientTrader ids aren't resolved, so only track text representations
      let info = event.info;
      let processRecordArguments = false;

      if (OpsIntVisibilityType.ORDER === type || CubiqTS.Long.isLong(orderId)) {
        if (info.orderData != null && info.orderData instanceof CubiqTS.OrderDataImpl) {
          let orderData = info.orderData;
          if (orderData.clientName != null) labelSet.add(orderData.clientName);
          if (orderData.clientCode != null) labelSet.add(orderData.clientCode);
          if (orderData.accountCode != null) labelSet.add(orderData.accountCode);
          if (orderData.accountName != null) labelSet.add(orderData.accountName);

          if (orderData.clientTraderName != null) labelSet.add(orderData.clientTraderName);
          orderData = this.orderDataPrivatizationService.checkPrivatization(orderData);
          if (StaticDataPrivatizationType.hasPrivatization(orderData)) {
            label = orderData.clientName;
            processRecordArguments = true;
          }
        }
      }

      // goal of this function is to establish client id name and redact matching event arguments with pritization value
      if (OpsIntVisibilityType.DEAL === type || CubiqTS.Long.isLong(dealId)) {
        if (info.deal != null && info.deal instanceof CubiqTS.DealImpl) {
          let deal = info.deal;
          if (deal.clientName != null) {
            labelSet.add(deal.clientName);
          }
          if (deal.accountName != null) {
            labelSet.add(deal.accountName);
          }
          if (deal.clientTraderName != null) {
            labelSet.add(deal.clientTraderName);
          }
          if (deal.clientCode != null) {
            labelSet.add(deal.clientCode);
          }
          if (deal.accountCode != null) {
            labelSet.add(deal.accountCode);
          }

          deal = this.dealPrivatizationService.checkPrivatization(deal);
          if (StaticDataPrivatizationType.hasPrivatization(deal)) {
            (label = deal.clientName), (processRecordArguments = true);
          }
        }
      }

      if (processRecordArguments) {
        OpsIntEventPrivatizer.setPending(event, [[label, labelSet]]);
      }
    }
    return event;
  }

  protected fireModelErrorEvent(event: CubiqTS.OpsIntEventImpl): void {
    if (logger.out.isDebugEnabled()) {
      logger.out.debug(
        `${
          this.constructor.name
        }.fireModelErrorEvent(${event.eventId.toString()} in response to a failed action`,
      );
    }

    let id = event.eventId.toString();
    if (this.base.hasOpsInt(id)) {
      super.fireModelErrorEvent(event);
    }
  }

  protected keyProperty(): string {
    return "eventId";
  }

  private fireRateEventNotificationEvent(event: any) {
    this.notificationService.fireNotificationEvent(
      this.notificationService.rateDataToNotification(
        event,
        BFXNotificationType.STREAM_OFF,
        BFXNotificationSubType.STREAM_OFF_STREAM_OFF,
      ),
      BFXNotificationEventType.OPS_INTERVENTION_UPDATE_EVENT,
    );
  }

  public getFilterCategoryTypes(): Promise<Map<string, CubiqTS.OpsFiltCategoryType>> {
    return new Promise<Map<string, CubiqTS.OpsFiltCategoryType>>((res, rej) => {
      res(this.base.getCategoryTypes());
    });
  }

  protected flushPendingUpdates(): void {
    if (this.queuedEvents != null && this.queuedEvents.length > 0) {
      logger.out.info(
        `${this.constructor.name}: Flushing ${this.queuedEvents.length} pending ops intervention event updates ...`,
      );

      this.queuedEvents.forEach((opsInterventionEvent: CubiqTS.OpsIntEventImpl) => {
        try {
          this.processOpsIntEvent(opsInterventionEvent, false);
        } catch (e) {
          logger.out.error(
            `${this.constructor.name}.processOpsIntEvent(${opsInterventionEvent}) failed with exception: ${e}`,
          );
        }
      });

      this.queuedEvents = [];
    }
  }
}
